import { z } from 'zod';
import {
    AUTOMATIC_SUPPORTED_UNITS_SCHEMA,
    DISPOSAL_PURPOSE_SCHEMA,
    FREIGHT_TYPE_SCHEMA,
    PRODUCT_TYPE_SCHEMA,
} from '@schuettflix/interfaces';
import { CurrencyCodeSchema } from '@schuettflix/utils';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { PriceSchema, PriceSchemaRules } from '@/shared/schemas/PriceSchema.ts';
import { VehicleClassSchema } from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { LocationConfigurationSchema } from '@/modules/location/domain/LocationConfigurationSchema.ts';
import { LocationTypeSchema } from '@/modules/location/domain/LocationSchema.ts';
import { DispositionFormDataSchema } from '@/modules/disposition/DispositionConfigurationPanel.tsx';
import { CarrierOrganizationIdSchema } from '@/shared/schemas/CarrierOrganizationIdSchema.ts';
import { getIsSalesTransportIncludedUnion } from './discriminatedUnions/getIsSalesTransportIncludedUnion.ts';
import { getIsPurchaseTransportIncludedUnion } from './discriminatedUnions/getIsPurchaseTransportIncludedUnion.ts';
import { getProductTypeUnion } from './discriminatedUnions/getProductTypeUnion.ts';
import { getFreightTypeUnion } from './discriminatedUnions/getFreightTypeUnion.ts';
import { getScopeUnion } from './discriminatedUnions/getScopeUnion.ts';
import { AdditionalServiceFormRule, getAdditionalServiceSchema } from './getAdditionalServiceSchema.ts';
import { isMissingLocationWithWeighingData, isWeighingLocationRequired } from '../domain/isWeighingLocationRequired.ts';
import { isQuantityBelowMinValue, MinValueRule } from '../domain/isQuantityBelowMinValue.ts';

type GetAutomaticProductsFormSchemaParams = {
    errorMessages?: {
        required?: string;
        quantityMinValue?: string;
        missingWeighingDataLocation?: string;
    };
    priceRules?: PriceSchemaRules;
    salesMaterialQuantity?: MinValueRule;
    salesTransportQuantity?: MinValueRule;
    additionalServices?: {
        [identifier: string]: AdditionalServiceFormRule;
    };
};

function getAutomaticProductsFormBaseSchema(params?: GetAutomaticProductsFormSchemaParams) {
    const required = params?.errorMessages?.required;
    const priceRules = params?.priceRules;

    return z.object({
        productType: PRODUCT_TYPE_SCHEMA,
        scope: z.enum(['POSITION', 'ORDER']).optional(),
        isStandalone: z.boolean(),

        // Product Identifiers
        materialIdentifier: z.string().optional(),
        transportIdentifier: z.string().optional(),

        // Product Details fields
        productName: z.string().trim().nonempty({ message: required }),
        currencyCode: CurrencyCodeSchema,
        freightType: FREIGHT_TYPE_SCHEMA.optional(),
        disposalPurpose: DISPOSAL_PURPOSE_SCHEMA.optional(),
        disposalCertificateNumber: z.string().trim().nonempty().optional(),

        // Sales Material fields
        salesMaterialQuantity: z.number().nonnegative().optional(),
        salesMaterialUnit: AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional(),
        salesMaterialPrice: PriceSchema().optional(),
        salesMaterialTaxClassId: z.string().trim().nonempty().optional(),
        showZeroSalesMaterialPrice: z.boolean().optional(),
        isSalesTransportIncluded: z.boolean(),

        // Sales Transport fields
        vehicleClass: VehicleClassSchema.optional(),
        salesTransportQuantity: z.number().nonnegative().optional(),
        salesTransportUnit: AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional(),
        salesTransportPrice: PriceSchema().optional(),
        salesTransportTaxClassId: z.string().trim().nonempty().optional(),
        showZeroSalesTransportPrice: z.boolean().optional(),

        // Purchase Material fields
        supplierOrganizationId: z.number().optional(),
        purchaseMaterialQuantity: z.number().nonnegative().optional(),
        purchaseMaterialUnit: AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional(),
        purchaseMaterialPrice: PriceSchema().optional(),
        purchaseMaterialTaxClassId: z.string().trim().nonempty().optional(),
        showZeroPurchaseMaterialPrice: z.boolean().optional(),
        isPurchaseTransportIncluded: z.boolean(),

        // Purchase Transport fields
        carrierOrganizationId: CarrierOrganizationIdSchema({
            excludedIds: [...EXCLUDED_PARTNER_ORGANIZATION_IDS],
            errorMessages: { required },
        }).optional(),
        purchaseTransportQuantity: z.number().nonnegative().optional(),
        purchaseTransportUnit: AUTOMATIC_SUPPORTED_UNITS_SCHEMA.optional(),
        purchaseTransportPrice: PriceSchema().optional(),
        purchaseTransportTaxClassId: z.string().trim().nonempty().optional(),
        showZeroPurchaseTransportPrice: z.boolean().optional(),

        // Locations fields
        loadingLocation: LocationConfigurationSchema.optional(),
        unloadingLocation: LocationConfigurationSchema.optional(),
        weighingLocation: LocationTypeSchema.nullish(),

        // Disposition fields
        disposition: DispositionFormDataSchema.optional(),
        // Seller product fields
        sellerProduct: z
            .object({
                sellerProductId: z.string(),
                productDefinitionId: z.string(),
                legacyFactoryId: z.number().optional(),
                name: z.string(),
            })
            .nullish(),

        // Order fields
        transportNumber: z.number().positive().optional(),

        // Additional services fields
        additionalServices: z.array(getAdditionalServiceSchema({ errorMessages: { required }, priceRules }).partial()),
    });
}

export function getAutomaticProductsFormSchema(params?: GetAutomaticProductsFormSchemaParams) {
    const required = params?.errorMessages?.required;
    const quantityMinValue = params?.errorMessages?.quantityMinValue ?? required;
    const missingWeighingDataLocation = params?.errorMessages?.missingWeighingDataLocation ?? required;
    const priceRules = params?.priceRules ?? {};
    const salesMaterialQuantityRule = params?.salesMaterialQuantity;
    const salesTransportQuantityRule = params?.salesTransportQuantity;
    const additionalServicesFormRules = params?.additionalServices;

    return (
        getAutomaticProductsFormBaseSchema(params)
            .and(getIsSalesTransportIncludedUnion({ formRules: { priceRules }, errorMessages: { required } }))

            .and(getIsPurchaseTransportIncludedUnion({ errorMessages: { required }, formRules: { priceRules } }))

            .and(getProductTypeUnion({ formRules: { priceRules }, errorMessages: { required } }))

            .and(getFreightTypeUnion({ errorMessages: { required } }))

            .and(getScopeUnion({ errorMessages: { required } }))

            // Sales Quantities Minimum Value Validation
            .superRefine((data, ctx) => {
                if (
                    isQuantityBelowMinValue({
                        quantity: data.salesMaterialQuantity,
                        unit: data.salesMaterialUnit,
                        rule: salesMaterialQuantityRule,
                    })
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: quantityMinValue,
                        path: ['salesMaterialQuantity'],
                    });
                }

                if (
                    isQuantityBelowMinValue({
                        quantity: data.salesTransportQuantity,
                        unit: data.salesTransportUnit,
                        rule: salesTransportQuantityRule,
                    })
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: quantityMinValue,
                        path: ['salesTransportQuantity'],
                    });
                }
            })

            // Location Validation
            .superRefine((data, ctx) => {
                if (data.isSalesTransportIncluded && data.isPurchaseTransportIncluded) {
                    if (!data.loadingLocation) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, message: required, path: ['loadingLocation'] });
                    }
                    if (!data.unloadingLocation) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, message: required, path: ['unloadingLocation'] });
                    }
                }
            })

            // additionalServices Validation
            .superRefine((data, ctx) => {
                const additionalServiceSchema = getAdditionalServiceSchema({ errorMessages: { required }, priceRules });

                data.additionalServices.forEach((service, index) => {
                    const result = additionalServiceSchema.safeParse(service);

                    if (!result.success) {
                        result.error.issues.forEach(issue => {
                            ctx.addIssue({
                                ...issue,
                                message: required,
                                path: ['additionalServices', index, ...issue.path],
                            });
                        });
                    }

                    const rule =
                        service.identifier !== undefined
                            ? additionalServicesFormRules?.[service.identifier]?.salesQuantity
                            : undefined;

                    if (isQuantityBelowMinValue({ quantity: service.salesQuantity, unit: service.salesUnit, rule })) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: quantityMinValue,
                            path: ['additionalServices', index, 'salesQuantity'],
                        });
                    }
                });
            })

            // weighingLocation Validation
            .superRefine((data, ctx) => {
                if (isWeighingLocationRequired(data) && !data.weighingLocation) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: required,
                        path: ['weighingLocation'],
                    });
                }

                if (isMissingLocationWithWeighingData(data)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: missingWeighingDataLocation,
                        path: ['weighingLocation'],
                    });
                }
            })
    );
}

export type AutomaticProductsFormValues = z.infer<ReturnType<typeof getAutomaticProductsFormSchema>>;
export type PartialAutomaticProductsFormValues = Partial<
    z.infer<ReturnType<typeof getAutomaticProductsFormBaseSchema>>
>;
